import { z } from 'zod';
import type { LocalizedText } from '../permissions.ts';
import { THEME_FONTS } from '../theme.ts';

/**
 * Acabamentos do Estúdio de estilos (ADR 0128): as peças que se repetem em toda tela. A letra usa
 * as fontes que a base já tem (a da marca ou as do aparelho; nada é baixado de fora).
 */
export const FINISH_OPTION_VALUES = {
  font: THEME_FONTS,
  headFont: ['same', 'base', 'modern', 'classic', 'friendly'],
  textSize: ['13', '14', '15'],
  headWeight: ['500', '600', '700'],
  tracking: ['tight', 'normal'],
  labelCase: ['upper', 'normal'],
  numFont: ['sans', 'mono'],
  radius: ['0', '2', '4', '6', '8', '10', '12', '14', '16'],
  btnShape: ['default', 'pill', 'square'],
  panel: ['shadow', 'border', 'both', 'flat'],
  activeMark: ['fill', 'bar', 'pill'],
  iconStyle: ['outline', 'duotone', 'solid'],
  iconStroke: ['1.5', '2', '2.5'],
  iconSize: ['16', '18', '20'],
  iconTint: ['muted', 'accent'],
  btnPrimary: ['solid', 'soft', 'outline'],
  btnSecondary: ['tonal', 'outline', 'ghost'],
  btnIcon: ['left', 'right', 'none'],
  input: ['outline', 'filled', 'underline'],
  labelPos: ['top', 'side'],
  badge: ['soft', 'solid', 'outline', 'dot'],
  tabs: ['underline', 'pill', 'segmented'],
  alert: ['soft', 'bar', 'solid'],
  tableHeader: ['filled', 'plain'],
  tableRows: ['lines', 'zebra', 'none'],
  tableBorder: ['outer', 'grid'],
  rowActions: ['menu', 'icons', 'hover'],
  headerRule: ['yes', 'no'],
  chartStyle: ['bar', 'line', 'area'],
  avatar: ['circle', 'rounded'],
  emptyStyle: ['illus', 'icon', 'plain'],
  docHeader: ['line', 'band'],
} as const;
export type FinishOptionKey = keyof typeof FINISH_OPTION_VALUES;
export type FinishOptions = {
  [K in FinishOptionKey]: (typeof FINISH_OPTION_VALUES)[K][number];
};

export const finishOptionsSchema = z
  .object(
    Object.fromEntries(
      Object.entries(FINISH_OPTION_VALUES).map(([k, v]) => [
        k,
        z.enum(v as unknown as [string, ...string[]]).optional(),
      ]),
    ),
  )
  .strip() as unknown as z.ZodType<Partial<FinishOptions>>;

export interface FinishSpec {
  id: string;
  name: LocalizedText;
  description: LocalizedText;
  values: FinishOptions;
}

/**
 * O Sóbrio é o acabamento de fábrica do beadev: os valores do tema padrão da base (o item marcado
 * no menu na cor de destaque, como a base sempre desenhou).
 */
const SOBRIO: FinishOptions = {
  font: 'base',
  headFont: 'same',
  textSize: '14',
  headWeight: '600',
  tracking: 'tight',
  labelCase: 'upper',
  numFont: 'sans',
  radius: '8',
  btnShape: 'default',
  panel: 'shadow',
  activeMark: 'pill',
  iconStyle: 'outline',
  iconStroke: '2',
  iconSize: '18',
  iconTint: 'muted',
  btnPrimary: 'solid',
  btnSecondary: 'tonal',
  btnIcon: 'left',
  input: 'outline',
  labelPos: 'top',
  badge: 'soft',
  tabs: 'underline',
  alert: 'soft',
  tableHeader: 'filled',
  tableRows: 'lines',
  tableBorder: 'outer',
  rowActions: 'menu',
  headerRule: 'yes',
  chartStyle: 'bar',
  avatar: 'circle',
  emptyStyle: 'illus',
  docHeader: 'line',
};

const F = (
  id: string,
  pt: [string, string],
  en: [string, string],
  values: Partial<FinishOptions>,
): FinishSpec => ({
  id,
  name: { 'pt-BR': pt[0], en: en[0] },
  description: { 'pt-BR': pt[1], en: en[1] },
  values: { ...SOBRIO, ...values },
});

export const FINISHES: readonly FinishSpec[] = [
  F(
    'sobrio',
    ['Sóbrio', 'Letra da marca, cantos discretos, sombra leve.'],
    ['Sober', 'Brand font, subtle corners, light shadow.'],
    {},
  ),
  F(
    'tecnico',
    ['Técnico', 'Números alinhados, grade e bordas.'],
    ['Technical', 'Aligned numbers, grid and borders.'],
    {
      font: 'modern',
      textSize: '13',
      labelCase: 'normal',
      numFont: 'mono',
      tracking: 'normal',
      radius: '2',
      panel: 'border',
      activeMark: 'bar',
      iconStroke: '1.5',
      iconSize: '16',
      input: 'filled',
      labelPos: 'side',
      badge: 'dot',
      alert: 'bar',
      tableRows: 'zebra',
      tableBorder: 'grid',
      rowActions: 'icons',
      headerRule: 'no',
      chartStyle: 'line',
      avatar: 'rounded',
      emptyStyle: 'plain',
    },
  ),
  F(
    'suave',
    ['Suave', 'Cantos redondos, ícones duotom.'],
    ['Soft', 'Round corners, duotone icons.'],
    {
      font: 'friendly',
      headWeight: '700',
      labelCase: 'normal',
      tracking: 'normal',
      radius: '12',
      btnShape: 'pill',
      activeMark: 'pill',
      iconStyle: 'duotone',
      iconTint: 'accent',
      input: 'filled',
      tabs: 'pill',
      tableHeader: 'plain',
      rowActions: 'hover',
      headerRule: 'no',
      chartStyle: 'area',
      docHeader: 'band',
    },
  ),
  F(
    'classico',
    ['Clássico', 'Títulos serifados, contornos e zebrado.'],
    ['Classic', 'Serif headings, outlines and zebra rows.'],
    {
      font: 'modern',
      headFont: 'classic',
      tracking: 'normal',
      radius: '4',
      panel: 'border',
      activeMark: 'bar',
      iconStroke: '1.5',
      btnSecondary: 'outline',
      btnIcon: 'none',
      badge: 'outline',
      alert: 'bar',
      tableRows: 'zebra',
      emptyStyle: 'icon',
    },
  ),
  F(
    'moderno',
    ['Moderno', 'Ícones cheios, abas em segmento.'],
    ['Modern', 'Solid icons, segmented tabs.'],
    {
      font: 'modern',
      headWeight: '700',
      labelCase: 'normal',
      panel: 'both',
      activeMark: 'pill',
      iconStyle: 'solid',
      iconTint: 'accent',
      btnSecondary: 'ghost',
      badge: 'solid',
      tabs: 'segmented',
      alert: 'solid',
      tableHeader: 'plain',
      tableRows: 'none',
      rowActions: 'hover',
      headerRule: 'no',
      chartStyle: 'area',
      avatar: 'rounded',
      emptyStyle: 'icon',
      docHeader: 'band',
    },
  ),
];
export const FINISH_IDS = FINISHES.map((f) => f.id) as [string, ...string[]];

export function finishSpec(id: string): FinishSpec | undefined {
  return FINISHES.find((f) => f.id === id);
}

/** Os ajustes do acabamento com os valores dele preenchidos. */
export function resolvedFinish(id: string, options?: Partial<FinishOptions>): FinishOptions | null {
  const spec = finishSpec(id);
  return spec ? { ...spec.values, ...options } : null;
}
